import { createContext, useState, useContext, useEffect } from 'react';
import { authService, getStoredUser, storeTokenAndScheduleRefresh } from 'services/api';

import { getCsrfToken } from 'services/csrf';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonationState, setImpersonationState] = useState(() => {
    const stored = localStorage.getItem('impersonation');
    return stored ? JSON.parse(stored) : null;
  });

  // Wrapper to persist user to localStorage when setting user
  const setUser = (userData) => {
    setUserState(userData);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      localStorage.removeItem('user');
    }
  };

  useEffect(() => {
    // Check if user is logged in on mount
    const storedUser = getStoredUser();
    if (storedUser) {
      setUser(storedUser);
      // Optionally verify token is still valid
      authService
        .getCurrentUser()
        .then((userData) => setUser(userData))
        .catch(() => {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    await getCsrfToken();
    const data = await authService.login(credentials);
    // Only set user if 2FA is not required and setup is not required
    if (!data.requires_two_factor && !data.requires_two_factor_setup && data.user) {
      setUser(data.user);
    }
    return data;
  };

  const refreshUser = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const setToken = (token, expiresInMinutes) => {
    storeTokenAndScheduleRefresh(token, expiresInMinutes);
  };

  const register = async (userData) => {
    await getCsrfToken();
    const data = await authService.register(userData);
    return data;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const updateUser = async (userData) => {
    const data = await authService.updateProfile(userData);
    setUser(data.user);
    return data;
  };

  const isAdmin = () => user?.role === 'admin';
  const isVendor = () => user?.role === 'vendor';
  const isClient = () => user?.role === 'client';
  const isApproved = () => user?.status === 'approved';

  const impersonateUser = async (userId) => {
    try {
      // Store current admin token and user
      const currentToken = localStorage.getItem('token');
      const currentUser = user;

      // Call impersonation API
      const response = await authService.impersonateUser(userId);

      // Store impersonation state
      const impersonation = {
        isImpersonating: true,
        originalAdminToken: currentToken,
        originalAdminUser: currentUser,
        adminInfo: response.admin
      };

      localStorage.setItem('impersonation', JSON.stringify(impersonation));
      setImpersonationState(impersonation);

      // Set new token and user
      localStorage.setItem('token', response.token);
      setUser(response.user);

      return response;
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      if (!impersonationState) {
        throw new Error('Not currently impersonating');
      }

      // Call stop impersonation API
      await authService.stopImpersonation();

      // Restore admin token and user
      localStorage.setItem('token', impersonationState.originalAdminToken);
      setUser(impersonationState.originalAdminUser);

      // Clear impersonation state
      localStorage.removeItem('impersonation');
      setImpersonationState(null);

      // Refresh user data to ensure it's current
      await refreshUser();
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      throw error;
    }
  };

  const value = {
    user,
    setUser,
    setToken,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    loading,
    isAuthenticated: !!user,
    isAdmin,
    isVendor,
    isClient,
    isApproved,
    impersonationState,
    impersonateUser,
    stopImpersonation,
    isImpersonating: !!impersonationState?.isImpersonating
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
